// Library Methods
import { useMemo } from "react";
import { HubConnection } from "@microsoft/signalr";

// Components
import Box from "@mui/material/Box";
import D15VisionLines from "src/components/containers/shared/ReportSections/ReportGraph/ColorVisionGraph/D15Graph/D15VisionLines/D15VisionLines";
import D15Options from "src/components/containers/shared/ReportSections/ReportGraph/ColorVisionGraph/D15Graph/D15Options/D15Options";
import NonVisualFieldLegend from "src/components/containers/shared/NonVisualFieldLegend/NonVisualFieldLegend";
import PelliRobsonGraph from "src/components/containers/shared/ReportSections/ReportGraph/ContrastSensitivityGraph/PelliRobsonGraph/PelliRobsonGraph";
import LandoltCGraph from "src/components/containers/shared/ReportSections/ReportGraph/VisualAcuityGraph/LandoltCGraph/LandoltCGraph";
import SnellenGraph from "src/components/containers/shared/ReportSections/ReportGraph/VisualAcuityGraph/SnellenGraph/SnellenGraph";
import ETDRSGraph from "src/components/containers/shared/ReportSections/ReportGraph/VisualAcuityGraph/ETDRSGraph/ETDRSGraph";
import BaileyLovieGraph from "src/components/containers/shared/ReportSections/ReportGraph/VisualAcuityGraph/BaileyLovieGraph/BaileyLovieGraph";
import ContrastTumblingGraph from "src/components/containers/shared/ReportSections/ReportGraph/ContrastSensitivityGraph/TumblingEGraph/TumblingEGraph";
import VisualAcuityTumblingGraph from "src/components/containers/shared/ReportSections/ReportGraph/VisualAcuityGraph/TumblingEGraph/TumblingEGraph";
import RAPDPupils from "./RAPDPupils/RAPDPupils";
import RAPDChart from "src/components/containers/shared/ReportSections/ReportGraph/RAPDGraph/RAPDChart/RAPDChart";
import Ishihara from "./Ishihara/Ishihara";
import OcularMotility from "./OcularMotility/OcularMotility";
import LensSimulation from "./LensSimulation/LensSimulation";
import Synoptophore from "./Synoptophore/Synoptophore";
import Aniseikonia from "./Aniseikonia/Aniseikonia";

// Contexts
import { useLiveUpdateContext } from "src/contexts/live-update/LiveUpdateContext";

// Hooks
import { useComponentDimensions } from "src/hooks/window/useComponentDimensions";

type NonVFGraphsType = {
  connection: HubConnection | null;
};

const NonVFGraphs = ({ connection }: NonVFGraphsType) => {
  const {
    inTutorial,
    examReportLive,
    isVisualAcuity,
    isColorVisionD15,
    isColorVisionIshihara,
    isPelliRobson,
    isTumbling,
    isLandoltC,
    isSnellen,
    isBaileyLovie,
    isETDRS,
    isRAPD,
    isOcularMotility,
    isSynoptophore,
    isAniseikonia,
    isIOLSimulation,
    isProgressiveSimulation,
  } = useLiveUpdateContext();

  // Measure this container so components like OcularMotility can scale up
  // to fill whatever space is actually available instead of always
  // rendering at their fixed default size.
  const dimensions = useComponentDimensions({
    componentId: "#non-vf-graphs-container",
  });

  const showNonVisualChartLegend = useMemo(
    () =>
      (isPelliRobson ||
        isTumbling ||
        isVisualAcuity ||
        isLandoltC ||
        isBaileyLovie ||
        isETDRS ||
        isSnellen) &&
      examReportLive,
    [
      isPelliRobson,
      isTumbling,
      isVisualAcuity,
      isLandoltC,
      isSnellen,
      isBaileyLovie,
      isETDRS,
      examReportLive,
    ]
  );

  return (
    <Box
      id="non-vf-graphs-container"
      display="flex"
      flexDirection="column"
      // "flex-start" instead of "center" -- centering a flex column that can
      // overflow its scrollable parent (Graphs.tsx) traps the top of the
      // content above an unreachable negative scroll position in some
      // browsers, so tall graphs (RAPD, Ishihara, etc.) couldn't be scrolled
      // to the top.
      justifyContent="flex-start"
      gap="20px"
      height="100%"
    >
      {showNonVisualChartLegend && <NonVisualFieldLegend isLiveUpdate />}

      {isColorVisionD15 && examReportLive && !inTutorial && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Box
              sx={{
                width: "100%",
                maxWidth: "550px",
                // Aspect ratio for the cropped vision lines picture
                aspectRatio: "370 / 270",
              }}
            >
              <D15VisionLines stateData={examReportLive.state} isLiveUpdate />
            </Box>
          </Box>
          <D15Options data={examReportLive} isLiveUpdate />
        </Box>
      )}

      {isColorVisionIshihara && examReportLive && !inTutorial && (
        <Ishihara examData={examReportLive} isLiveUpdate />
      )}

      {isPelliRobson && examReportLive && !inTutorial && (
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <PelliRobsonGraph examData={examReportLive} />
        </Box>
      )}

      {isTumbling && !isVisualAcuity && examReportLive && !inTutorial && (
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <ContrastTumblingGraph examData={examReportLive} />
        </Box>
      )}

      {isTumbling && isVisualAcuity && examReportLive && (
        <VisualAcuityTumblingGraph examData={examReportLive} isLiveUpdate />
      )}

      {isLandoltC && examReportLive && (
        <LandoltCGraph examData={examReportLive} isLiveUpdate />
      )}

      {isSnellen && examReportLive && (
        <SnellenGraph examData={examReportLive} isLiveUpdate />
      )}

      {isBaileyLovie && examReportLive && (
        <BaileyLovieGraph examData={examReportLive} isLiveUpdate />
      )}

      {isETDRS && examReportLive && (
        <ETDRSGraph examData={examReportLive} isLiveUpdate />
      )}

      {isRAPD && examReportLive && (
        <>
          <RAPDChart
            chartData={examReportLive?.state}
            displayTitle={false}
            isLiveUpdate
          />
          <RAPDPupils chartData={examReportLive?.state} />
        </>
      )}

      {isOcularMotility && examReportLive && !inTutorial && (
        <OcularMotility
          examData={examReportLive}
          containerSize={dimensions?.dynamicWidth}
        />
      )}

      {isSynoptophore && examReportLive && !inTutorial && (
        <Synoptophore examData={examReportLive} />
      )}

      {isAniseikonia && examReportLive && !inTutorial && (
        <Aniseikonia examData={examReportLive} />
      )}

      {isIOLSimulation && examReportLive && !inTutorial && (
        <LensSimulation
          examData={examReportLive}
          productType="IOL"
          connection={connection}
        />
      )}

      {isProgressiveSimulation && examReportLive && !inTutorial && (
        <LensSimulation
          examData={examReportLive}
          productType="Progressive"
          connection={connection}
        />
      )}
    </Box>
  );
};

export default NonVFGraphs;
